import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Descriptions, Button, Table, Tag, Spin, message, Statistic } from 'antd';
import { ArrowLeftOutlined, UserOutlined, PhoneOutlined, MailOutlined, EnvironmentOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';

const DoctorDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [doctor, setDoctor] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDoctorDetails();
    }, [id]);

    const fetchDoctorDetails = async () => {
        try {
            const response = await api.get(`/companyseller/doctor/${id}`);
            setDoctor(response.data.data);
        } catch (error) {
            message.error('Failed to load doctor details');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!doctor) {
        return (
            <Card>
                <p>{t('doctorNotFound') || 'Doctor not found'}</p>
            </Card>
        );
    }

    const recentPatientsColumns = [
        {
            title: t('name') || 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('phoneNumber') || 'Phone Number',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
        },
        {
            title: t('age') || 'Age',
            dataIndex: 'age',
            key: 'age',
        },
        {
            title: t('gender') || 'Gender',
            dataIndex: 'gender',
            key: 'gender',
            render: (gender) => (
                <Tag color={gender === 'male' ? 'blue' : 'pink'}>
                    {gender}
                </Tag>
            )
        },
        {
            title: t('registeredDate') || 'Registered Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => new Date(date).toLocaleDateString()
        }
    ];

    return (
        <div>
            <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/companyseller/doctors')}
                style={{ marginBottom: 16 }}
            >
                {t('back') || 'Back to Doctors'}
            </Button>

            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card title={t('doctorInformation') || 'Doctor Information'}>
                        <Descriptions bordered column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}>
                            <Descriptions.Item label={t('name') || 'Name'}>
                                <UserOutlined /> {doctor.name}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('phoneNumber') || 'Phone Number'}>
                                <PhoneOutlined /> {doctor.phoneNumber}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('email') || 'Email'}>
                                <MailOutlined /> {doctor.email || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('specialization') || 'Specialization'}>
                                {doctor.specialization || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('address') || 'Address'} span={2}>
                                <EnvironmentOutlined /> {doctor.address || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('language') || 'Language'}>
                                {doctor.language?.toUpperCase()}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('status') || 'Status'}>
                                <Tag color={doctor.isActive ? 'green' : 'red'}>
                                    {doctor.isActive ? (t('active') || 'Active') : (t('inactive') || 'Inactive')}
                                </Tag>
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                </Col>

                {doctor.settings && (
                    <Col span={24}>
                        <Card title={t('clinicInformation') || 'Clinic Information'}>
                            <Descriptions bordered column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}>
                                <Descriptions.Item label={t('clinicName') || 'Clinic Name'}>
                                    {doctor.settings.doctorName}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('clinicPhone') || 'Clinic Phone'}>
                                    {doctor.settings.clinicPhone}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('clinicAddress') || 'Clinic Address'} span={2}>
                                    {doctor.settings.clinicAddress}
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </Col>
                )}

                <Col xs={24} sm={12} lg={8}>
                    <Card>
                        <Statistic
                            title={t('totalPatients') || 'Total Patients'}
                            value={doctor.patientCount || 0}
                            prefix={<UserOutlined />}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                    <Card>
                        <Statistic
                            title={t('totalVisits') || 'Total Visits'}
                            value={doctor.visitCount || 0}
                            prefix={<UserOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                    <Card>
                        <Statistic
                            title={t('registeredDate') || 'Registered Date'}
                            value={new Date(doctor.createdAt).toLocaleDateString()}
                            valueStyle={{ fontSize: 16 }}
                        />
                    </Card>
                </Col>

                {doctor.recentPatients && doctor.recentPatients.length > 0 && (
                    <Col span={24}>
                        <Card title={t('recentPatients') || 'Recent Patients'}>
                            <Table
                                columns={recentPatientsColumns}
                                dataSource={doctor.recentPatients}
                                rowKey="_id"
                                pagination={{ pageSize: 5 }}
                            />
                        </Card>
                    </Col>
                )}
            </Row>
        </div>
    );
};

export default DoctorDetails;
